import React from "react";

// Same brand asset used for the big truck — crop the truck portion.
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_register-samples/artifacts/7jqxsms7_WhatsApp%20Image%202026-06-18%20at%2013.21.40.jpeg";

/**
 * Silk Road journey map — Pakistan → India → Silk Road → Macclesfield.
 * Animated dashed pink trail with a small Hair Drips truck moving along it.
 */
export const JourneyMap = ({ className = "" }) => {
  // Path between landmarks; coordinates in 800x340 viewbox.
  const pathD =
    "M 60 250 C 140 200, 210 280, 280 240 S 420 180, 480 200 S 640 120, 740 140";

  return (
    <div className={`relative ${className}`} data-testid="journey-map">
      <svg
        viewBox="0 0 800 340"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-auto"
        aria-label="Hair Drips journey from Pakistan to Macclesfield"
      >
        {/* Faded contour map shapes (sandy decorative landmasses) */}
        <g opacity="0.18" fill="#c9a27a">
          <path d="M -10 290 Q 80 200, 180 260 T 360 240 T 560 220 T 820 200 L 820 360 L -10 360 Z" />
          <path d="M 20 110 Q 120 80, 220 120 T 420 90 T 620 130 T 820 100 L 820 0 L 20 0 Z" />
        </g>

        {/* Decorative landmark icons — detailed line illustrations */}
        {/* Pakistan: Badshahi Mosque (Lahore) */}
        <g transform="translate(38 188)" stroke="#a86b35" strokeWidth="1.2" fill="none" opacity="0.7" strokeLinecap="round" strokeLinejoin="round">
          {/* base platform */}
          <path d="M -4 44 L 48 44" strokeWidth="1.6" />
          <path d="M -2 44 L -2 38 L 46 38 L 46 44" />
          {/* left minaret */}
          <path d="M 2 38 L 2 14 L 6 14 L 6 38" />
          <circle cx="4" cy="11" r="3" />
          <path d="M 4 8 L 4 4" strokeWidth="0.8" />
          <circle cx="4" cy="3" r="0.8" fill="#a86b35" />
          {/* right minaret */}
          <path d="M 38 38 L 38 14 L 42 14 L 42 38" />
          <circle cx="40" cy="11" r="3" />
          <path d="M 40 8 L 40 4" strokeWidth="0.8" />
          <circle cx="40" cy="3" r="0.8" fill="#a86b35" />
          {/* main building */}
          <path d="M 10 38 L 10 22 L 34 22 L 34 38" />
          {/* main dome (onion) */}
          <path d="M 12 22 Q 12 8, 22 8 Q 32 8, 32 22 Z" />
          <path d="M 22 8 L 22 2" strokeWidth="0.8" />
          <circle cx="22" cy="1.5" r="0.9" fill="#a86b35" />
          {/* side small domes */}
          <path d="M 10 22 Q 10 16, 14 16 Q 18 16, 18 22" />
          <path d="M 26 22 Q 26 16, 30 16, Q 34 16, 34 22" />
          {/* central arch */}
          <path d="M 18 38 L 18 30 Q 22 24, 26 30 L 26 38" />
          {/* side arches */}
          <path d="M 12 38 L 12 32 Q 14 28, 16 32 L 16 38" strokeWidth="0.8" />
          <path d="M 28 38 L 28 32 Q 30 28, 32 32 L 32 38" strokeWidth="0.8" />
        </g>

        {/* India: Taj Mahal */}
        <g transform="translate(248 190)" stroke="#a86b35" strokeWidth="1.2" fill="none" opacity="0.7" strokeLinecap="round" strokeLinejoin="round">
          {/* base platform */}
          <path d="M -6 44 L 54 44" strokeWidth="1.6" />
          <path d="M -4 44 L -4 40 L 52 40 L 52 44" />
          <path d="M 0 40 L 0 36 L 48 36 L 48 40" />
          {/* four corner minarets */}
          {[0, 14, 34, 46].map((x, i) => (
            <g key={i}>
              <path d={`M ${x} 36 L ${x} 18 L ${x + 2} 18 L ${x + 2} 36`} />
              <circle cx={x + 1} cy="16" r="2" />
              <path d={`M ${x + 1} 14 L ${x + 1} 10`} strokeWidth="0.6" />
              <circle cx={x + 1} cy="9" r="0.7" fill="#a86b35" />
            </g>
          ))}
          {/* main building */}
          <path d="M 8 36 L 8 26 L 40 26 L 40 36" />
          {/* main dome */}
          <path d="M 12 26 Q 14 6, 24 4 Q 34 6, 36 26 Z" />
          {/* dome top spire */}
          <path d="M 24 4 L 24 -3" strokeWidth="0.8" />
          <circle cx="24" cy="-4" r="1" fill="#a86b35" />
          {/* small side domes */}
          <path d="M 8 26 Q 8 18, 12 18, Q 16 18, 16 26" />
          <path d="M 32 26 Q 32 18, 36 18, Q 40 18, 40 26" />
          {/* arch entrance */}
          <path d="M 20 36 L 20 30 Q 24 24, 28 30 L 28 36" />
          <path d="M 21 36 L 21 31 Q 24 27, 27 31 L 27 36" strokeWidth="0.6" />
        </g>

        {/* Silk Road: detailed mountains with snow caps */}
        <g transform="translate(420 138)" stroke="#a86b35" strokeWidth="1.2" fill="none" opacity="0.7" strokeLinecap="round" strokeLinejoin="round">
          {/* back range */}
          <path d="M 0 50 L 12 22 L 22 36 L 32 12 L 46 40 L 58 28 L 70 50 Z" />
          {/* snow caps */}
          <path d="M 8 30 L 12 22 L 16 30" stroke="#fff" strokeWidth="1.4" opacity="0.9" />
          <path d="M 28 18 L 32 12 L 36 18" stroke="#fff" strokeWidth="1.4" opacity="0.9" />
          {/* front range */}
          <path d="M 4 50 L 18 32 L 28 46 L 40 26 L 52 42 L 64 32 L 70 50" />
          {/* shading lines */}
          <path d="M 12 22 L 14 40" strokeWidth="0.7" />
          <path d="M 32 12 L 36 36" strokeWidth="0.7" />
          {/* sun */}
          <circle cx="62" cy="14" r="3" fill="#f26b1e" opacity="0.5" stroke="none" />
        </g>

        {/* Silk Road: detailed camel with rider */}
        <g transform="translate(560 168)" stroke="#a86b35" strokeWidth="1.2" fill="none" opacity="0.7" strokeLinecap="round" strokeLinejoin="round">
          {/* body */}
          <path d="M 4 30 Q 10 12, 18 26 Q 22 8, 30 26 L 36 26 L 36 36" />
          {/* belly */}
          <path d="M 4 30 L 4 36" />
          <path d="M 4 36 L 36 36" />
          {/* legs */}
          <path d="M 6 36 L 6 46" />
          <path d="M 12 36 L 12 46" />
          <path d="M 26 36 L 26 46" />
          <path d="M 32 36 L 32 46" />
          {/* hooves */}
          <path d="M 5 46 L 7 46" strokeWidth="1.6" />
          <path d="M 11 46 L 13 46" strokeWidth="1.6" />
          <path d="M 25 46 L 27 46" strokeWidth="1.6" />
          <path d="M 31 46 L 33 46" strokeWidth="1.6" />
          {/* neck and head */}
          <path d="M 36 26 L 42 18 L 46 14 Q 50 12, 50 16 L 48 20" />
          {/* ear */}
          <path d="M 46 14 L 48 11" strokeWidth="0.8" />
          {/* eye */}
          <circle cx="48" cy="15" r="0.5" fill="#a86b35" />
          {/* tail */}
          <path d="M 4 30 L 0 28 L -1 32" strokeWidth="0.8" />
          {/* saddle/cargo */}
          <path d="M 14 22 L 14 14 L 26 14 L 26 22" strokeWidth="0.9" />
          <path d="M 16 14 L 24 14" strokeWidth="0.6" />
        </g>

        {/* Macclesfield: detailed clock tower (St Michael's / town hall style) */}
        <g transform="translate(710 92)" stroke="#a86b35" strokeWidth="1.2" fill="none" opacity="0.75" strokeLinecap="round" strokeLinejoin="round">
          {/* base */}
          <path d="M 4 58 L 28 58" strokeWidth="1.6" />
          <path d="M 6 58 L 6 50 L 26 50 L 26 58" />
          {/* main shaft */}
          <path d="M 8 50 L 8 28 L 24 28 L 24 50" />
          {/* shaft windows */}
          <path d="M 12 44 L 12 38 L 14 38 L 14 44 Z" />
          <path d="M 18 44 L 18 38 L 20 38 L 20 44 Z" />
          {/* clock face */}
          <circle cx="16" cy="22" r="5" />
          {/* clock hands */}
          <path d="M 16 22 L 16 19" strokeWidth="0.8" />
          <path d="M 16 22 L 19 22" strokeWidth="0.8" />
          {/* clock ticks */}
          <circle cx="16" cy="18" r="0.3" fill="#a86b35" />
          <circle cx="16" cy="26" r="0.3" fill="#a86b35" />
          <circle cx="12" cy="22" r="0.3" fill="#a86b35" />
          <circle cx="20" cy="22" r="0.3" fill="#a86b35" />
          {/* tower belfry */}
          <path d="M 6 17 L 26 17" strokeWidth="1.6" />
          <path d="M 8 17 L 8 12 L 24 12 L 24 17" />
          <path d="M 14 17 L 14 13 Q 16 11, 18 13 L 18 17" strokeWidth="0.7" />
          {/* spire */}
          <path d="M 6 12 L 16 -2 L 26 12" />
          <path d="M 16 -2 L 16 -8" strokeWidth="0.8" />
          {/* finial cross */}
          <path d="M 16 -8 L 16 -12 M 14 -10 L 18 -10" strokeWidth="0.8" />
          <circle cx="16" cy="-7" r="0.6" fill="#a86b35" />
        </g>

        {/* The trail */}
        <path
          d={pathD}
          fill="none"
          stroke="#e5197f"
          strokeWidth="3"
          strokeLinecap="round"
          className="trail-path"
        />

        {/* Section labels */}
        <text x="50" y="285" fontFamily="Bowlby One" fontSize="14" fill="#0b1b3d">PAKISTAN</text>
        <text x="247" y="265" fontFamily="Bowlby One" fontSize="12" fill="#0b1b3d">INDIA</text>
        <text x="430" y="135" fontFamily="Bowlby One" fontSize="12" fill="#0b1b3d">THE SILK ROAD</text>
        <text x="660" y="100" fontFamily="Bowlby One" fontSize="12" fill="#0b1b3d">SILK TOWN</text>
        <text x="660" y="115" fontFamily="Bowlby One" fontSize="12" fill="#e5197f">MACCLESFIELD</text>

        {/* Pins */}
        <g>
          <circle cx="60" cy="250" r="7" fill="#e5197f" stroke="#fff" strokeWidth="2" />
          <circle cx="740" cy="140" r="7" fill="#e5197f" stroke="#fff" strokeWidth="2" />
        </g>

        {/* Moving mini truck (image clipped to truck region of brand logo asset) */}
        <foreignObject
          x="-26"
          y="-16"
          width="52"
          height="32"
          style={{
            offsetPath: `path('${pathD}')`,
            offsetRotate: "0deg",
            animation: "truckDrive 14s linear infinite",
          }}
        >
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            style={{
              width: "52px",
              height: "32px",
              backgroundImage: `url('${LOGO_URL}')`,
              backgroundSize: `${(1290 / 370) * 100}% ${(1215 / 225) * 100}%`,
              backgroundPosition: `${(415 / (1290 - 370)) * 100}% ${(680 / (1215 - 225)) * 100}%`,
              backgroundRepeat: "no-repeat",
              filter: "drop-shadow(0 2px 3px rgba(11,27,61,0.25))",
            }}
            aria-hidden="true"
          />
        </foreignObject>
      </svg>
    </div>
  );
};

export default JourneyMap;
